import * as turf from "@turf/turf";
import cn from "classnames";
import L from "leaflet";
import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { LayerAlias, MapModes, MapToolsModes, UnitTypes } from "constant";
import features from "features";
import { convertPercentagesToNumber } from "helpers/convertPercentagesToNumber";
import { ensureClosedPolygon } from "helpers/ensureClosedPolygon";
import { RootStateInterface } from "reducer";
import { UnitsManagerStateInterface } from "scenes/UnitsManager/interfaces";
import { MapStateInterface } from "../../ducks";
import { useMapContext } from "context/MapContextProvider";

import IconButton from "components/buttons/IconButton/IconButton";
import MapSearchPoint from "../../components/MapSearchPoint/MapSearchPoint";

import CursorWhiteArrowIcon from "assets/icons/cursor-white-arrow.svg";
import { ReactComponent as PenToolIcon } from "assets/icons/pen-tool.svg";
import PlusIcon from "assets/icons/plus-black.svg";

import styles from "./map-toolbar.module.scss";

const MapToolbar = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const toolbarRef = useRef(null);
  const { setPointCoordinates } = useMapContext();

  const { mapToolsMode, mapMode } = useSelector<
    RootStateInterface,
    MapStateInterface
  >((state) => state.map);

  const { creationPolygonParameters } = useSelector<
    RootStateInterface,
    UnitsManagerStateInterface
  >((state) => state.unitsManager);

  useEffect(() => {
    if (toolbarRef.current) {
      L.DomEvent.disableClickPropagation(toolbarRef.current);
      L.DomEvent.disableScrollPropagation(toolbarRef.current);
    }
  }, []);

  const onToolClick = useCallback(
    (mode: MapModes) => () => {
      setPointCoordinates(null);
      if (!creationPolygonParameters.id && mode !== MapModes.POLYGON_CREATION) {
        if (creationPolygonParameters.geo_data.coordinates.length > 2) {
          const polygonCoordinates = ensureClosedPolygon(
            creationPolygonParameters.geo_data.coordinates
          );

          const polygon = turf.polygon([polygonCoordinates]);

          const areaInSquareMeters = turf.area(polygon);

          const fields = {
            ...creationPolygonParameters,
            geo_data: {
              ...creationPolygonParameters.geo_data,
              coordinates: polygonCoordinates
            },
            additional: {
              ...creationPolygonParameters.additional,
              area: areaInSquareMeters,
              stroke_transparency: convertPercentagesToNumber(
                creationPolygonParameters.additional.stroke_transparency
              ),
              fill_transparency: convertPercentagesToNumber(
                creationPolygonParameters.additional.fill_transparency
              )
            }
          };

          dispatch(
            features.unitsManager.actions.createUnitRequest({
              params: {
                projectId: id,
                alias: LayerAlias.MANUAL,
                type: UnitTypes.POLYGON
              },
              fields,
              onSuccess: () => {
                dispatch(
                  features.unitsManager.actions.clearCreationPolygonHistory()
                );
                dispatch(
                  features.unitsManager.actions.clearCreationPolygonParameters()
                );
              }
            })
          );
        } else {
          dispatch(
            features.unitsManager.actions.clearCreationPolygonParameters()
          );
        }
      } else {
        dispatch(
          features.unitsManager.actions.clearCreationPolygonParameters()
        );
        dispatch(
          features.unitsManager.actions.setAllPolygonsVisibility({
            projectId: id,
            isVisible: true
          })
        );
      }
      dispatch(features.map.actions.setMapMode(mode));
    },
    [dispatch, creationPolygonParameters, id, setPointCoordinates]
  );

  return (
    <div className={styles["map-toolbar"]} ref={toolbarRef}>
      <IconButton
        icon={CursorWhiteArrowIcon}
        alt="cursor-white-arrow-icon"
        className={cn(styles["icon-button"], {
          [styles["icon-active-button"]]: mapMode === MapModes.DEFAULT
        })}
        onClick={onToolClick(MapModes.DEFAULT)}
      />
      <IconButton
        icon={<PenToolIcon />}
        alt="pen-tool-icon"
        className={cn(styles["icon-button"], {
          [styles["icon-active-button"]]:
            mapMode === MapModes.POLYGON_CREATION ||
            mapMode === MapModes.MISSION_POLYGON_CREATION
        })}
        onClick={onToolClick(
          mapToolsMode === MapToolsModes.LAYERS
            ? MapModes.POLYGON_CREATION
            : MapModes.MISSION_POLYGON_CREATION
        )}
      />
      <div className={styles["separator"]} />
      <MapSearchPoint onSearchClick={onToolClick} />
      <IconButton
        icon={PlusIcon}
        alt="plus-icon"
        className={styles["icon-button"]}
      />
    </div>
  );
};

export default MapToolbar;
